import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { T } from '@tolgee/react';
import { OrderInterface } from '../../../../../components/OrdersTable/interfaces/Order.interface';
import { SypacButton, SypacText } from '@sypac/component-library-react';
import { MapIcon } from '../../../../../assets/MapIcon';
import HideMap from '../../../../../assets/HideMap';
import Map from '../../../../../components/Map/Map';
import {
	MapMarker,
	MapPolyline,
	PinVariant,
} from '../../../../../components/Map/Map.interface';
import { DriverDto } from '../../../../../services/truck.services';
import {
	GeoService,
	ResponnseRouteResponse,
} from '../../../../../services/geo.services';
import { OrderStatusesEnum } from '../../../../../services/orders.services';
import { OrderTarget } from '../../../../../components/OrdersTable/interfaces/OrderStatus.interface';
import WarningIcon from '../../../../../assets/WarningIcon';

interface TruckDetailsMapProps {
	selectedOrder: OrderInterface | undefined;
	allocatedTruck?: DriverDto;
	target: string;
}

const TruckDetailsMap: React.FC<TruckDetailsMapProps> = (props) => {
	const { selectedOrder, allocatedTruck, target } = props;
	const [markers, setMarkers] = useState<MapMarker[]>([]);
	const [loadingMap, setLoading] = useState<boolean>(false);
	const [isMapOpen, setIsMapOpen] = useState<boolean>(true);
	const [polygons, setPolygons] = useState<MapPolyline[]>([]);

	const lastKeyRef = useRef<string | null>(null);

	const drivers = useMemo<DriverDto[]>(() => {
		const seen = new Set<string | number>();
		const out: DriverDto[] = [];
		for (const t of selectedOrder?.trucks ?? []) {
			const d = t?.driver;
			if (!d) continue;
			const key = d.id as unknown as string | number;
			if (!seen.has(key)) {
				seen.add(key);
				out.push(d);
			}
		}
		return out;
	}, [selectedOrder?.trucks]);

	const getRouteAndMarkers = useCallback(async () => {
		try {
			const pickupCoords = selectedOrder?.pickupLocation?.coordinates || [
				28.7929007, 47.0431905,
			];
			const deliveryCoords = selectedOrder?.deliveryLocation?.coordinates || [
				19.4116509, 51.7738652,
			];
			const truckCoords = selectedOrder?.truck?.lastLocation?.coordinates ||
				allocatedTruck?.lastLocation?.coordinates || [28.80058, 47.04014];

			const pickup = { lat: pickupCoords[1], lng: pickupCoords[0] };
			const delivery = { lat: deliveryCoords[1], lng: deliveryCoords[0] };
			const truck = { lat: truckCoords[1], lng: truckCoords[0] };

			const baseMarkers: MapMarker[] = [
				{
					latitude: pickup.lat,
					longitude: pickup.lng,
					variant: PinVariant.pickup,
					pickupAddress: selectedOrder?.pickupAddress,
				},
				{
					latitude: delivery.lat,
					longitude: delivery.lng,
					variant: PinVariant.delivery,
					deliveryAddress: selectedOrder?.deliveryAddress,
				},
			];

			if (
				selectedOrder?.status &&
				![
					OrderStatusesEnum.DELIVERED.toString(),
					OrderStatusesEnum.COMPLETED.toString(),
				].includes(selectedOrder.status) &&
				selectedOrder?.truck
			) {
				baseMarkers.push({
					latitude: truck.lat,
					longitude: truck.lng,
					variant: PinVariant.truck,
					truck: selectedOrder.truck,
				});
			}

			setMarkers(baseMarkers);

			const [truckToPickupRes, pickupToDeliveryRes] = await Promise.all([
				GeoService.getRoute({
					points: [`${truck.lat},${truck.lng}`, `${pickup.lat},${pickup.lng}`],
				}),
				GeoService.getRoute({
					points: [
						`${pickup.lat},${pickup.lng}`,
						`${delivery.lat},${delivery.lng}`,
					],
				}),
			]);

			const parseRoute = (
				data: ResponnseRouteResponse,
				routeIndex: number,
				id: number,
			): MapPolyline | null => {
				const route = data?.data?.[routeIndex];
				if (!route) return null;
				const shape: [number, number][] = JSON.parse(route.shape || '[]');
				return {
					id,
					orderRoute: true,
					distance: route.distance || 0,
					trafficTime: route.trafficTime || 0,
					coordinates: shape.map(([lat, lng]) => ({ lat, lng })),
				};
			};

			const polygonTruckToPickup = parseRoute(truckToPickupRes, 0, 0);
			const polygonPickupToDelivery = parseRoute(pickupToDeliveryRes, 0, 1);
			const polygonPickupToDeliveryAlt = parseRoute(pickupToDeliveryRes, 1, 2);

			const validPolygons = [
				polygonTruckToPickup,
				polygonPickupToDelivery,
				polygonPickupToDeliveryAlt,
			].filter(Boolean) as MapPolyline[];

			const lastPoint =
				polygonPickupToDelivery?.coordinates?.[
					(polygonPickupToDelivery?.coordinates?.length || 1) - 1
				];

			const EPS = 0.0002;
			const needConnector =
				!!lastPoint &&
				(Math.abs(lastPoint.lat - delivery.lat) > EPS ||
					Math.abs(lastPoint.lng - delivery.lng) > EPS);

			const connector = needConnector
				? ({
						id: 999,
						orderRoute: false,
						distance: 0,
						trafficTime: 0,
						coordinates: [lastPoint, { lat: delivery.lat, lng: delivery.lng }],
						connector: true,
				  } as any)
				: null;

			const finalPolygons = connector
				? [...validPolygons, connector]
				: validPolygons;
			setPolygons(finalPolygons);
		} catch (err) {
			console.error('Error fetching routes and markers:', err);
		}
	}, [selectedOrder]);

	const markersMap = useCallback(() => {
		setLoading(true);

		const all: DriverDto[] = [
			...drivers,
			...(allocatedTruck ? [allocatedTruck] : []),
		].filter((d): d is DriverDto => Boolean(d && d.id));

		const byId = new globalThis.Map<string | number, DriverDto>();
		for (const d of all) {
			const key = d.id as unknown as string | number;
			byId.set(key, d);
		}
		const uniqueById: DriverDto[] = Array.from(byId.values());

		const toNum = (v: unknown, fallback: number) => {
			const n = typeof v === 'string' ? parseFloat(v) : (v as number);
			return Number.isFinite(n) ? n : fallback;
		};

		const truckMarkers: MapMarker[] = uniqueById
			.map((r) => ({
				latitude: toNum(r?.lastLocation?.coordinates?.[1], 47.04014),
				longitude: toNum(r?.lastLocation?.coordinates?.[0], 28.80058),
				variant: PinVariant.truck,
				truck: r,
			}))
			.filter(
				(m) => Number.isFinite(m.latitude) && Number.isFinite(m.longitude),
			);

		setMarkers((prev) => {
			const staticPins = prev.filter((m) => m.variant !== PinVariant.truck);
			return [...staticPins, ...truckMarkers];
		});

		setTimeout(() => setLoading(false), 300);
	}, [allocatedTruck, drivers]);

	useEffect(() => {
		markersMap();
	}, [markersMap]);

	useEffect(() => {
		if (target !== OrderTarget.transporter) return;

		const key = JSON.stringify({
			id: selectedOrder?.id ?? null,
			status: selectedOrder?.status ?? null,
			pickup: selectedOrder?.pickupLocation?.coordinates ?? null,
			delivery: selectedOrder?.deliveryLocation?.coordinates ?? null,
			truck: selectedOrder?.truck?.lastLocation?.coordinates ?? null,
		});

		if (lastKeyRef.current === key) return;
		lastKeyRef.current = key;

		getRouteAndMarkers().catch((err) =>
			console.error('Error fetching routes and markers:', err),
		);
	}, [getRouteAndMarkers, selectedOrder?.id, target]);

	const toggleMap = () => {
		setIsMapOpen(!isMapOpen);
	};

	return (
		<div className="bg-white border border-solid border-gray-10 rounded-xl">
			{isMapOpen && (
				<div className="flex justify-between px-5 pt-4 mb-4">
					<SypacText variant="body-normal-medium">
						<p className="text-gray-60">
							<T keyName="detailsMap.routeDetails">Route details</T>
						</p>
					</SypacText>

					<div className="flex items-center gap-3 cursor-pointer ml-auto">
						<span className="flex scale-[1.1]">
							<HideMap />
						</span>
						<SypacButton variant="secondary" size="small">
							<button
								className="w-full border-none bg-transparent p-0 group"
								type="button"
								onClick={toggleMap}
							>
								<SypacText variant="body-normal-medium">
									<p className="transition group-hover:text-gray-80">
										<T keyName="acceptedStep.hideMap">Hide map</T>
									</p>
								</SypacText>
							</button>
						</SypacButton>
					</div>
				</div>
			)}

			{isMapOpen ? (
				<div className="h-[320px] px-5 mb-3">
					<div
						className={`w-full h-full ${
							loadingMap ? 'bg-gray-1' : 'bg-transparent'
						}`}
					>
						{!loadingMap ? <Map markers={markers} polygons={polygons} /> : null}
					</div>
				</div>
			) : (
				<SypacButton
					variant="secondary"
					size="large"
					className="box-border w-full px-5 py-4"
				>
					<button
						className="w-full h-[42px] bg-alabaster rounded-10 px-5 pt-4 transition"
						type="button"
						onClick={toggleMap}
					>
						<span className="mt-[4px] scale-[1.2]">
							<MapIcon />
						</span>
						<SypacText variant="body-normal-medium">
							<p className="transition group-hover:text-gray-80">
								<T keyName="acceptedStep.showMap">Show map</T>
							</p>
						</SypacText>
					</button>
				</SypacButton>
			)}

			<div className="w-fit flex items-center gap-[6px] py-[2px] pl-1 pr-3 ml-5 mb-4 bg-white border border-solid border-gray-10 rounded-[6px]">
				<WarningIcon />
				<SypacText variant="body-normal-medium">
					<p className="text-[12px] leading-[16px] text-gray-60">
						<T keyName="detailsMap.routeGenerated">
							Route generated based on: Truck Route and Shortest Route criteria.
						</T>
					</p>
				</SypacText>
			</div>
		</div>
	);
};

export default TruckDetailsMap;
